import React, { useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';

import { Heading, Text, Button } from '@grupoboticario/flora-react';

import content from 'src/common/content';

import { useStyle } from 'src/contexts/StyleContext';

import RevistaIcons from 'src/assets/icons';
import { ArrowForward, CloseOutlined } from '@material-ui/icons';

import Footer from 'src/components/organisms/Footer';

import Styled from 'src/styles/pages/AcessibilidadeStyled';

import { handleAccessibilityMenuClick } from 'src/common/helpers/analyticsEvents';

export const GoToCatalogButton = () => {
  const router = useRouter();

  const goToAccessibleCatalog = () => {
    handleAccessibilityMenuClick('visitar-catalogo-acessivel');
    router.push('/');
  };

  return (
    <Styled.GoToCatalogButton>
      <Button
        variant="ghost"
        rightIcon={<ArrowForward />}
        data-testid="go-to-accessible-catalog"
        onClick={goToAccessibleCatalog}
      >
        {`Visitar ${content.platform.toLowerCase()} acessível`}
      </Button>
    </Styled.GoToCatalogButton>
  );
};

const Acessibilidade = () => {
  const router = useRouter();

  const { style, toggleContrast, toggleFont, getStyle } = useStyle();

  useEffect(getStyle, []);

  const handleHome = useCallback(
    (eventLabel: string) => () => {
      handleAccessibilityMenuClick(eventLabel);
      router.back();
    },
    [],
  );

  const handleToggleFont = useCallback(() => {
    toggleFont();
    const eventLabel = `${style.fontLarge ? 'diminuir' : 'aumentar'}-fonte`;
    handleAccessibilityMenuClick(eventLabel);
  }, [style.fontLarge]);

  const handleToggleContrast = useCallback(() => {
    toggleContrast();
    const eventLabel = `${style.contrast ? 'diminuir' : 'aumentar'}-contraste`;
    handleAccessibilityMenuClick(eventLabel);
  }, [style.contrast]);

  return (
    <Styled.Wrapper data-testid="accessibility-page">
      <Head>
        <title>
          Acessibilidade: {content.platform} Digital - {content.name}
        </title>
        <meta
          name="description"
          content={`Quer conhecer os principais destaques, lançamentos e promoções de ${content.name}? Confira o nosso ${content.platform} Digital Acessível, afinal, beleza é para todos!`}
        ></meta>
        <link rel="canonical" href={`${content.urlBase}/acessibilidade/`} />
      </Head>
      <Styled.CloseButtonContainer>
        <Button
          variant="alt"
          leftIcon={<CloseOutlined />}
          onClick={handleHome('fechar-menu')}
          data-testid="close-button"
        >
          Fechar opções de acessibilidade
        </Button>
      </Styled.CloseButtonContainer>
      <Styled.Main className="main-content">
        <Styled.Title>
          <Heading as="h1" level="6" color="$dark-dark">
            Somos acessíveis.
            <br aria-hidden="true" />
            Afinal, beleza é para todos.
          </Heading>
        </Styled.Title>

        <GoToCatalogButton />

        <hr aria-hidden="true" />

        <Styled.OptionsSubtitle>
          <Text as="h2" size="action" color="$dark-dark">
            Ajustes de visibilidade
          </Text>
        </Styled.OptionsSubtitle>

        <Styled.ButtonsContainer>
          <Styled.Button
            data-testid="accessibilityMenu-increaseFontSize-button"
            active={style.fontLarge}
            onClick={handleToggleFont}
          >
            <RevistaIcons name="font" />
            <Text as="span" size="action">
              {style.fontLarge ? 'Diminuir' : 'Aumentar'} fonte
            </Text>
          </Styled.Button>
          <Styled.Button
            data-testid="accessibilityMenu-highContrast-button"
            active={style.contrast}
            onClick={handleToggleContrast}
          >
            <RevistaIcons name="contrast" />
            <Text as="span" size="action">
              {style.contrast ? 'Diminuir' : 'Aumentar'} contraste
            </Text>
          </Styled.Button>
        </Styled.ButtonsContainer>

        <hr aria-hidden="true" />

        <Styled.Instructions>
          <Heading as="h3" level="6" color="$dark-dark">
            Utilize os seguintes atalhos para navegar pelo {content.platform}:
          </Heading>

          <Text as="ul" size="bodyMediumShort">
            <li>Alt + 1 para Página de Acessibilidade</li>
            <li>Alt + 2 para ir para o conteúdo da revista</li>
            <li>Alt + 3 para baixar o PDF da revista</li>
            <li>Alt + 0 para ir para a próxima página da revista</li>
            <li>Alt + 9 para ir para a página anterior da revista</li>
            <li>Ou utilize a tecla TAB. </li>
          </Text>

          <Text
            as="div"
            size="bodyMediumShort"
            css={{
              margin: '$0',
              padding: '$2 $0',
            }}
          >
            A navegabilidade pelo {content.platform.toLowerCase()} tem melhor
            desempenho com as versões mais atuais dos leitores de tela.
          </Text>
        </Styled.Instructions>

        <GoToCatalogButton />
      </Styled.Main>

      <Footer />
    </Styled.Wrapper>
  );
};

export default Acessibilidade;
